import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import confetti from 'canvas-confetti';

const SocketContext = createContext();

export { SocketContext };

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Helper: build a consistent conversation key from two user IDs
const getConversationKey = (uid1, uid2) => {
  return [uid1, uid2].sort().join('_');
};

// Helper: optimistically update session cache from socket events.
// No immediate invalidation — the socket event data is authoritative,
// and useSession already refetches every 30s for consistency.
const updateSessionCache = (queryClient, sessionId, updater) => {
  if (!sessionId) return;
  const queryKey = ['session', sessionId];

  queryClient.setQueryData(queryKey, (oldData) => {
    if (!oldData) return oldData; // no cached session yet, skip
    return updater(oldData);
  });
};

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const socketRef = useRef(null);
  const currentSessionRef = useRef(null);
  // Which tab the user is looking at — used to mute toasts for content
  // they can already see (no chat toast while reading the chat)
  const activeViewRef = useRef('playlist');

  // DM state
  const [directMessages, setDirectMessages] = useState({}); // conversationKey -> messages[]
  const [dmConversations, setDmConversations] = useState([]); // list of active DM conversations
  const [unreadDMs, setUnreadDMs] = useState({}); // recipientId -> unreadCount

  // Rolling feed of party moments for the live activity ticker
  const [activity, setActivity] = useState([]);
  const activitySeq = useRef(0);

  // Weighted, time-decaying events feeding the Party Energy meter
  const [energyEvents, setEnergyEvents] = useState([]);
  const pushEnergy = useCallback((weight) => {
    setEnergyEvents((prev) => [...prev.slice(-99), { ts: Date.now(), w: weight }]);
  }, []);

  const ENERGY_WEIGHTS = useRef({
    '🔥': 15, // join
    '💡': 10, // suggestion
    '🎵': 12, // direct add
    '✅': 12, // approval
    '👍': 6,
    '👎': 3,
    '⏭️': 4,
    '📷': 8,
    '🎧': 8
  });

  const pushActivity = useCallback((icon, text) => {
    activitySeq.current += 1;
    const entry = { id: activitySeq.current, icon, text, ts: Date.now() };
    setActivity((prev) => [...prev.slice(-19), entry]);
    const weight = ENERGY_WEIGHTS.current[icon];
    if (weight) pushEnergy(weight);
  }, [pushEnergy]);

  // Live emoji reactions floating over the session (ephemeral)
  const [reactions, setReactions] = useState([]);

  // Initialize socket connection
  useEffect(() => {
    if (user && !socketRef.current) {
      const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:3007', {
        autoConnect: false,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      // Socket event listeners
      newSocket.on('connect', async () => {
        console.log('Socket connected');
        setConnected(true);

        // Authenticate with the server
        if (user) {
          try {
            const token = user.getIdToken ? await user.getIdToken() : `mock-token-${user.uid}`;
            newSocket.emit('authenticate', {
              token: token,
              sessionId: currentSession
            });
          } catch (error) {
            console.error('Failed to get auth token:', error);
          }
        }
      });

      newSocket.on('disconnect', () => {
        console.log('Socket disconnected');
        setConnected(false);
      });

      newSocket.on('reconnect', async () => {
        console.log('Socket reconnected');
        setConnected(true);
        // Re-authenticate and rejoin session if needed
        try {
          const token = user.getIdToken ? await user.getIdToken() : `mock-token-${user.uid}`;
          // Rejoin happens in the 'authenticated' handler — emitting
          // joinSession here would race the server's async auth
          newSocket.emit('authenticate', { token, sessionId: currentSession });
        } catch (e) {
          console.error('Re-auth failed:', e);
        }

        // Force full refetch to catch any events missed during disconnect
        queryClient.invalidateQueries(['session']);
      });

      newSocket.on('connect_error', (err) => {
        console.warn('Socket connect_error:', err?.message);
      });

      newSocket.on('authenticated', (data) => {
        console.log('Socket authenticated:', data);
        // (Re)join the session room only after the server has finished
        // authenticating this socket, and restore the DM conversation list
        if (currentSessionRef.current) {
          newSocket.emit('joinSession', { sessionId: currentSessionRef.current });
          newSocket.emit('getDMConversations');
        }
      });

      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
        toast.error(error.message || 'Connection error');
      });

      // ---------------------------------------------------------------
      // Session events — optimistic cache updates
      // ---------------------------------------------------------------

      newSocket.on('userJoined', (data) => {
        console.log('User joined:', data);
        pushActivity('🔥', `${data.user.displayName} joined the party`);
        toast.success(`${data.user.displayName} joined the session`, { duration: 2000 });

        // The join moment: a quick confetti burst for everyone already here
        if (
          data.user.uid !== user?.uid &&
          document.visibilityState === 'visible' &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          confetti({
            particleCount: 50,
            spread: 75,
            startVelocity: 28,
            origin: { y: 0.25 },
            colors: ['#BA2BE2', '#E0369B', '#FF6B00', '#FFC107'],
            disableForReducedMotion: true
          });
        }

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          // Add / update the participant entry
          if (data.user.uid) {
            participants[data.user.uid] = {
              ...participants[data.user.uid],
              ...data.user,
              joinedAt: data.timestamp || new Date().toISOString(),
              isVisible: true,
              isBlocked: false
            };
          }
          return { ...session, participants };
        });
      });

      newSocket.on('userLeft', (data) => {
        console.log('User left:', data);
        // No toast — leaving is ambient information, the People tab shows it

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          if (data.user.uid) {
            delete participants[data.user.uid];
          }
          return { ...session, participants };
        });
      });

      newSocket.on('songSuggested', (data) => {
        console.log('Song suggested:', data);
        pushActivity('💡', `${data.suggestedBy.displayName} suggested "${data.song.name}"`);
        // Only the people who can act on a suggestion (host/co-hosts) get
        // notified — guests see it appear in the Suggestions tab
        const cached = queryClient.getQueryData(['session', data.sessionId]);
        const canAct = cached &&
          (cached.hostId === user?.uid || cached.participants?.[user?.uid]?.role === 'cohost');
        if (canAct && data.suggestedBy?.uid !== user?.uid) {
          toast(`${data.suggestedBy.displayName} suggested "${data.song.name}"`, {
            icon: '🎵',
            duration: 3500
          });
        }

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const suggestions = { ...(session.suggestions || {}) };
          // The server creates a suggestion object with id, song, suggestedBy, timestamp, votes
          suggestions[data.id] = {
            id: data.id,
            song: data.song,
            suggestedBy: data.suggestedBy,
            timestamp: data.timestamp,
            votes: data.votes || 0
          };
          return { ...session, suggestions };
        });
      });

      newSocket.on('songVoted', (data) => {
        console.log('Song voted:', data);
        pushActivity(data.vote === 1 ? '👍' : '👎', `${data.userInfo.displayName} ${data.vote === 1 ? 'upvoted' : 'downvoted'} a song`);
        // No toast — votes show up live on the playlist itself

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const votes = { ...(session.votes || {}) };
          // votes structure: votes[songId][userId] = vote value
          votes[data.songId] = { ...(votes[data.songId] || {}), [data.userId]: data.vote };
          return { ...session, votes };
        });
      });

      newSocket.on('messageReceived', (data) => {
        // Toast only for messages you can't already see: not your own,
        // and not while you're looking at the chat tab
        const isOwn = (data.sender?.uid || data.senderId) === user?.uid;
        if (data.isVisible !== false && !isOwn && activeViewRef.current !== 'chat') {
          const preview = data.type === 'photo' ? '📷 Photo' : data.message;
          toast(`${data.sender?.displayName || data.senderName}: ${preview}`, {
            icon: '💬',
            duration: 3000
          });
        }

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const messages = { ...(session.messages || {}) };
          messages[data.id] = data;
          return { ...session, messages };
        });
      });

      // Photo events
      newSocket.on('photoShared', (data) => {
        pushActivity('📷', `${data.senderName} shared a photo`);
        if (data.senderId !== user?.uid && activeViewRef.current !== 'photos') {
          toast(`${data.senderName} shared a photo`, { icon: '📷', duration: 2500 });
        }
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const photos = { ...(session.photos || {}) };
          photos[data.id] = data;
          return { ...session, photos };
        });
      });

      newSocket.on('photoDeleted', (data) => {
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const photos = { ...(session.photos || {}) };
          delete photos[data.photoId];
          return { ...session, photos };
        });
      });

      newSocket.on('userVisibilityChanged', (data) => {
        console.log('User visibility changed:', data);
        // No toast — visibility is reflected in the People tab

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          if (data.userId && participants[data.userId]) {
            participants[data.userId] = {
              ...participants[data.userId],
              isVisible: data.isVisible
            };
          }
          return { ...session, participants };
        });
      });

      // Host control events
      newSocket.on('userBlocked', (data) => {
        console.log('User blocked:', data);
        // Only the blocked user gets the toast; everyone updates the cache
        if (data.userId === user?.uid) {
          toast.error('You have been blocked by the host');
        }
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          if (participants[data.userId]) {
            participants[data.userId] = { ...participants[data.userId], isBlocked: true };
          }
          return { ...session, participants };
        });
      });

      newSocket.on('userUnblocked', (data) => {
        console.log('User unblocked:', data);
        if (data.userId === user?.uid) {
          toast.success('You have been unblocked by the host');
        }
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          if (participants[data.userId]) {
            participants[data.userId] = { ...participants[data.userId], isBlocked: false };
          }
          return { ...session, participants };
        });
      });

      newSocket.on('songRemoved', (data) => {
        console.log('Song removed:', data);
        // Quiet — the row disappears from the Suggestions tab live

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const suggestions = { ...(session.suggestions || {}) };
          delete suggestions[data.songId];
          return { ...session, suggestions };
        });
      });

      newSocket.on('songApproved', (data) => {
        console.log('Song approved:', data);
        pushActivity('✅', `"${data.playlistSong.name}" made the playlist`);
        if (data.playlistSong.addedBy?.uid === user?.uid) {
          // The moment the guest has been waiting for
          toast.success(`Your song "${data.playlistSong.name}" was approved! 🎉`, { duration: 4000 });
        } else {
          toast(`"${data.playlistSong.name}" added to playlist`, { icon: '🎵', duration: 2500 });
        }

        updateSessionCache(queryClient, data.sessionId, (session) => {
          // Remove from suggestions
          const suggestions = { ...(session.suggestions || {}) };
          delete suggestions[data.originalSuggestionId];
          // Add to playlist
          const playlist = { ...(session.playlist || {}) };
          playlist[data.playlistSong.id] = data.playlistSong;
          return { ...session, suggestions, playlist };
        });
      });

      // Playlist management events
      newSocket.on('songBoosted', (data) => {
        console.log('Song boosted:', data);
        toast(`Host boosted "${data.song.name}"`, {
          icon: '\uD83D\uDE80',
          duration: 3000
        });

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          if (playlist[data.songId]) {
            playlist[data.songId] = {
              ...playlist[data.songId],
              hostBoosted: true,
              boostTimestamp: new Date().toISOString()
            };
          }
          return { ...session, playlist };
        });
      });

      newSocket.on('playNextSet', (data) => {
        console.log('Play next set:', data);
        toast(`"${data.song.name}" will play next`, {
          icon: '\u23ED\uFE0F',
          duration: 3000
        });

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          // Clear playNext from all songs, set it on the target
          Object.keys(playlist).forEach((id) => {
            if (id === data.songId) {
              playlist[id] = { ...playlist[id], playNext: true, playNextTimestamp: new Date().toISOString() };
            } else if (playlist[id].playNext) {
              const { playNext, playNextTimestamp, ...rest } = playlist[id];
              playlist[id] = rest;
            }
          });
          return { ...session, playlist };
        });
      });

      newSocket.on('skipVoteAdded', (data) => {
        console.log('Skip vote added:', data);
        pushActivity('⏭️', `${data.user.displayName} voted to skip (${data.skipCount}/${data.requiredVotes})`);
        toast(`${data.user.displayName} voted to skip`, {
          icon: '\u23E9',
          duration: 2000
        });

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const skipVotes = { ...(session.skipVotes || {}) };
          skipVotes[data.songId] = {
            ...(skipVotes[data.songId] || {}),
            _count: data.skipCount,
            _required: data.requiredVotes
          };
          return { ...session, skipVotes };
        });
      });

      newSocket.on('songSkipped', (data) => {
        console.log('Song skipped:', data);
        pushActivity('⏭️', `The crowd skipped "${data.song?.name || 'a song'}"`);
        toast.success(`Skipped "${data.song.name}" - moving to next song`);

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          delete playlist[data.songId];
          // Also clean up skip votes for this song
          const skipVotes = { ...(session.skipVotes || {}) };
          delete skipVotes[data.songId];
          return { ...session, playlist, skipVotes };
        });
      });

      newSocket.on('playlistReordered', (data) => {
        console.log('Playlist reordered:', data);
        // Silent update - no toast for automatic reordering
        updateSessionCache(queryClient, data.sessionId, (session) => {
          if (data.playlist) {
            return { ...session, playlist: data.playlist };
          }
          return session;
        });
      });

      newSocket.on('songRemovedFromPlaylist', (data) => {
        console.log('Song removed from playlist:', data);
        toast(`"${data.song.name}" removed from playlist`, {
          icon: '\uD83D\uDDD1\uFE0F',
          duration: 3000
        });

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          delete playlist[data.songId];
          return { ...session, playlist };
        });
      });

      // Live session settings changes from the host's control panel
      newSocket.on('sessionSettingsUpdated', (data) => {
        updateSessionCache(queryClient, data.sessionId, (session) => ({
          ...session,
          settings: { ...(session.settings || {}), ...data.settings }
        }));
      });

      // Co-host promotions/demotions
      newSocket.on('roleChanged', (data) => {
        if (data.role === 'cohost') {
          const cachedS = queryClient.getQueryData(['session', data.sessionId]);
          const nm = cachedS?.participants?.[data.userId]?.displayName;
          if (nm) pushActivity('🎧', `${nm} is now a co-host`);
        }
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const participants = { ...(session.participants || {}) };
          if (participants[data.userId]) {
            participants[data.userId] = { ...participants[data.userId], role: data.role };
          }
          return { ...session, participants };
        });

        if (data.userId === user?.uid) {
          if (data.role === 'cohost') {
            toast.success("You're now a co-host! You can add songs directly and manage the queue.", { icon: '🎧', duration: 5000 });
          } else {
            toast('You are no longer a co-host', { duration: 4000 });
          }
        }
      });

      // Now-playing changed — the host's player is the source of truth,
      // the server persists it and rebroadcasts to the whole room
      newSocket.on('nowPlayingUpdated', (data) => {
        // The moment that matters: if the new track is one YOU brought,
        // the room hears your pick — celebrate it (once per track change)
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const newKey = data.nowPlaying?.songId;
          const prevKey = session.nowPlaying?.songId;
          if (newKey && newKey !== prevKey) {
            const startedSong = session.playlist?.[newKey];
            if (startedSong?.addedBy?.uid && startedSong.addedBy.uid === user?.uid) {
              toast.success(`Your song is playing — "${startedSong.name}"`, {
                icon: '🎉',
                duration: 5000
              });
            }
          }
          return session;
        });
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          if (data.playedSongId && playlist[data.playedSongId]) {
            playlist[data.playedSongId] = {
              ...playlist[data.playedSongId],
              status: 'played'
            };
          }
          // Bulk catch-up from a native host that played through songs
          // while backgrounded
          (data.playedSongIds || []).forEach((playedId) => {
            if (playlist[playedId]) {
              playlist[playedId] = { ...playlist[playedId], status: 'played' };
            }
          });
          if (data.nowPlaying?.songId && playlist[data.nowPlaying.songId]) {
            playlist[data.nowPlaying.songId] = {
              ...playlist[data.nowPlaying.songId],
              status: 'playing'
            };
          }
          return { ...session, playlist, nowPlaying: data.nowPlaying || null };
        });
      });

      // Song-level reaction toggled somewhere in the room
      newSocket.on('songReactionUpdated', (data) => {
        updateSessionCache(queryClient, data.sessionId, (session) => {
          const songReactions = { ...(session.songReactions || {}) };
          const perSong = { ...(songReactions[data.songId] || {}) };
          if (data.reacted) {
            perSong[data.uid] = true;
          } else {
            delete perSong[data.uid];
          }
          songReactions[data.songId] = perSong;
          return { ...session, songReactions };
        });
      });

      // Host direct add event
      newSocket.on('songAddedDirectly', (data) => {
        console.log('Song added directly by host:', data);
        pushActivity('🎵', `${data.addedBy?.displayName || 'Host'} added "${data.song.name}"`);
        // The person who added it already got their own confirmation
        if (data.addedBy?.uid !== user?.uid) {
          toast(`${data.addedBy?.displayName || 'Host'} added "${data.song.name}"`, {
            icon: '🎵',
            duration: 2500
          });
        }

        updateSessionCache(queryClient, data.sessionId, (session) => {
          const playlist = { ...(session.playlist || {}) };
          if (data.song.id) {
            playlist[data.song.id] = data.song;
          }
          return { ...session, playlist };
        });
      });

      // ---------------------------------------------------------------
      // Direct Message events
      // ---------------------------------------------------------------

      newSocket.on('directMessageReceived', (data) => {
        console.log('Direct message received:', data);
        const { message: dmMsg, sender, recipient } = data;
        const otherUserId = sender.uid === user?.uid ? recipient.uid : sender.uid;
        const otherUserName = sender.uid === user?.uid ? recipient.displayName : sender.displayName;
        const convKey = getConversationKey(sender.uid, recipient.uid);

        // Add message to conversation
        setDirectMessages((prev) => {
          const existing = prev[convKey] || [];
          // Deduplicate by message id
          if (existing.some((m) => m.id === dmMsg.id)) return prev;
          return { ...prev, [convKey]: [...existing, dmMsg] };
        });

        // Update conversations list
        setDmConversations((prev) => {
          const filtered = prev.filter((c) => c.recipientId !== otherUserId);
          return [
            {
              recipientId: otherUserId,
              recipientName: otherUserName,
              lastMessage: dmMsg.message,
              lastTimestamp: dmMsg.timestamp,
              conversationKey: convKey
            },
            ...filtered
          ];
        });

        // Increment unread count if the message is from someone else
        if (sender.uid !== user?.uid) {
          setUnreadDMs((prev) => ({
            ...prev,
            [sender.uid]: (prev[sender.uid] || 0) + 1
          }));

          toast(`DM from ${sender.displayName}: ${dmMsg.message}`, {
            duration: 4000,
            icon: '\u2709\uFE0F'
          });
        }
      });

      // Server-computed conversation list with unread counts — requested on
      // join/reconnect so DMs survive a page refresh
      // Floating emoji reactions
      newSocket.on('reactionReceived', (data) => {
        const id = `${data.ts}-${Math.random().toString(36).slice(2, 6)}`;
        const entry = { id, emoji: data.emoji, x: 15 + Math.random() * 70 };
        pushEnergy(5);
        setReactions((prev) => [...prev.slice(-30), entry]);
        setTimeout(() => {
          setReactions((prev) => prev.filter((r) => r.id !== id));
        }, 2400);
      });

      newSocket.on('dmConversations', (data) => {
        const convos = data.conversations || [];
        setDmConversations(convos.map((c) => ({
          recipientId: c.recipientId,
          recipientName: c.recipientName,
          lastMessage: c.lastMessage,
          lastTimestamp: c.lastTimestamp,
          conversationKey: c.conversationKey
        })));

        const unread = {};
        convos.forEach((c) => {
          if (c.unreadCount > 0) unread[c.recipientId] = c.unreadCount;
        });
        setUnreadDMs(unread);
      });

      newSocket.on('directMessageHistory', (data) => {
        console.log('Direct message history received:', data);
        const { recipientId, messages: historyMessages } = data;
        if (!recipientId || !user?.uid) return;
        const convKey = getConversationKey(user.uid, recipientId);

        setDirectMessages((prev) => ({
          ...prev,
          [convKey]: historyMessages || []
        }));
      });

      // Connect to socket
      newSocket.connect();

      // Heartbeat to keep presence alive
      const hb = setInterval(async () => {
        try {
          if (newSocket.connected && currentSessionRef.current) {
            const token = user.getIdToken ? await user.getIdToken() : `mock-token-${user.uid}`;
            newSocket.emit('heartbeat', { sessionId: currentSessionRef.current, token });
          }
        } catch {}
      }, 15000);
      newSocket.__hb = hb;
    }

    return () => {
      if (socketRef.current) {
        if (socketRef.current.__hb) {
          clearInterval(socketRef.current.__hb);
        }
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Join session
  const joinSession = (sessionId) => {
    if (socket && connected) {
      socket.emit('joinSession', { sessionId });
      setCurrentSession(sessionId);
      currentSessionRef.current = sessionId;
      // Restore the DM conversation list (server processes events in order)
      socket.emit('getDMConversations');
    }
  };

  // Leave session
  const leaveSession = () => {
    if (socket && connected && currentSession) {
      socket.emit('leaveSession', { sessionId: currentSession });
      setCurrentSession(null);
      currentSessionRef.current = null;
    }
  };

  // Suggest song
  const suggestSong = (song) => {
    if (socket && connected && currentSession) {
      console.log('[SocketContext] Suggesting song for session:', currentSession, song);
      socket.emit('suggestSong', {
        sessionId: currentSession,
        song
      });
    } else {
      console.error('[SocketContext] Cannot suggest song - missing:', {
        connected,
        currentSession,
        song
      });
    }
  };

  // Vote on song
  const voteSong = (songId, vote) => {
    if (socket && connected && currentSession) {
      socket.emit('voteSong', {
        sessionId: currentSession,
        songId,
        vote
      });
    }
  };

  // Send message
  const sendMessage = (message, isVisible = true) => {
    if (socket && connected && currentSession) {
      socket.emit('sendMessage', {
        sessionId: currentSession,
        message,
        isVisible
      });
    }
  };

  // Toggle visibility
  const toggleVisibility = (isVisible) => {
    if (socket && connected) {
      socket.emit('toggleVisibility', { isVisible });
    }
  };

  // Host actions. `extra` carries action-specific payloads
  // (e.g. { settings } for updateSettings, { role } for setRole)
  const hostAction = (action, targetUserId, songId, extra = {}) => {
    if (socket && connected && currentSession) {
      console.log('[SocketContext] Host action:', { action, songId, currentSession });
      socket.emit('hostAction', {
        action,
        targetUserId,
        songId,
        sessionId: currentSession,
        ...extra
      });
    } else {
      console.error('[SocketContext] Cannot perform host action - missing:', {
        connected,
        currentSession,
        action
      });
    }
  };

  // Send direct message
  const sendDirectMessage = useCallback((recipientId, message) => {
    if (socket && connected && currentSession) {
      socket.emit('sendDirectMessage', {
        sessionId: currentSession,
        recipientId,
        message
      });
    }
  }, [socket, connected, currentSession]);

  // Get direct message history
  const getDirectMessages = useCallback((recipientId) => {
    if (socket && connected && currentSession) {
      socket.emit('getDirectMessages', {
        sessionId: currentSession,
        recipientId
      });
    }
  }, [socket, connected, currentSession]);

  // Mark DMs as read
  const markDMRead = useCallback((recipientId) => {
    if (socket && connected && currentSession) {
      socket.emit('markDMRead', {
        sessionId: currentSession,
        recipientId
      });
    }
    // Clear local unread count immediately
    setUnreadDMs((prev) => {
      const next = { ...prev };
      delete next[recipientId];
      return next;
    });
  }, [socket, connected, currentSession]);

  // Total unread DM count (for badge)
  const totalUnreadDMs = Object.values(unreadDMs).reduce((sum, count) => sum + count, 0);

  // Session view reports which tab is active so toasts can stay out of the way
  const setActiveView = useCallback((view) => {
    activeViewRef.current = view;
  }, []);

  // Fire-and-forget emoji reaction
  const sendReaction = useCallback((emoji) => {
    if (socket && connected && currentSession) {
      socket.emit('sendReaction', { emoji });
    }
  }, [socket, connected, currentSession]);

  // 🔥 a queued song — costless appreciation, toggles per user
  const reactToSong = useCallback((songId) => {
    if (socket && connected && currentSession) {
      socket.emit('reactToSong', { sessionId: currentSession, songId });
    }
  }, [socket, connected, currentSession]);

  const value = {
    socket,
    connected,
    currentSession,
    joinSession,
    leaveSession,
    suggestSong,
    voteSong,
    reactToSong,
    sendMessage,
    toggleVisibility,
    hostAction,
    setActiveView,
    activity,
    energyEvents,
    reactions,
    sendReaction,
    // DM functions and state
    sendDirectMessage,
    getDirectMessages,
    markDMRead,
    directMessages,
    dmConversations,
    unreadDMs,
    totalUnreadDMs,
    getConversationKey: useCallback((uid1, uid2) => getConversationKey(uid1, uid2), [])
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
